import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

import {
  getBanks,
  getQualificationGrades,
  getUserCV,
  getUserEducations,
} from "../../store/profile/actions";
import {
  addInternshipApplication,
  getInternshipApplications,
} from "../../store/applicant/actions";
import { getTime } from "../../store/comm/actions";

import ProfileSummary from "./profile/components/ProfileStatus";
import EducationFormDialog from "./profile/components/EducationFormDialog";
import PreviewPdfUrl from "../comm/PreviewPdfUrl";

const VALID_DEGREE_IDS = [3, 4, 5, 7, 8];
const STEPS = [
  "Education Background",
  "Curriculum Vitae",
  "Bank Details",
  "Review & Submit",
];

const DEGREE_LABELS = { 3: "A1", 4: "A1", 5: "A0", 7: "Master's", 8: "PhD" };

export const NewInternshipApplication = (props) => {
  const {
    loading,
    lookup,
    getQualificationGrades,
    getUserEducations,
    getUserCV,
    getTime,
    addInternshipApplication,
    getInternshipApplications,
    getBanks,
    user,
    userEducations,
    userCV,
    internshipApplications,
  } = props;

  const history = useHistory();

  const [activeStep, setActiveStep] = useState(0);
  const [serverTime, setServerTime] = useState(null);

  // Step 1 — education
  const [selectedEducation, setSelectedEducation] = useState(null);
  const [qualificationGrade, setQualificationGrade] = useState(null);
  const [graduateDate, setGraduateDate] = useState(null);
  const [showAddEducation, setShowAddEducation] = useState(false);

  // Step 2 — CV
  const [selectedCV, setSelectedCV] = useState(null);

  // Step 3 — bank
  const [bank, setBank] = useState(null);
  const [accountNumber, setAccountNumber] = useState(
    user?.intern?.accountNumber || "",
  );

  // Preview
  const [showDocument, setShowDocument] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);

  const [formError, setFormError] = useState(null);

  useEffect(() => {
    document.title = "New Internship Application | Jobs";
  }, []);

  useEffect(() => {
    if (!lookup.qualificationGrades) getQualificationGrades();
    if (!userEducations?.length) getUserEducations();
    if (!userCV) getUserCV();
    if (!lookup.banks) getBanks();
    getInternshipApplications("", () => {});
    getTime((success, data) => {
      if (success) setServerTime(data);
    });
  }, []);

  useEffect(() => {
    if (lookup?.banks && user?.intern?.bankId) {
      const found = lookup.banks.find((b) => b.id === user.intern.bankId);
      if (found) setBank(found);
    }
  }, [lookup?.banks, user?.intern?.bankId]);

  // Auto-select profile CV when it loads
  useEffect(() => {
    if (userCV && !selectedCV) setSelectedCV(userCV);
  }, [userCV]);

  const getCurrentYear = () =>
    serverTime
      ? new Date(serverTime).getFullYear()
      : new Date().getFullYear();

  const usedDocRefIds = new Set(
    (internshipApplications || [])
      .map((app) => app.degreeReferenceDocId)
      .filter(Boolean),
  );

  const eligibleEducations = (userEducations || []).filter((edu) => {
    const degreeId = edu.qualification?.degree?.id ?? edu.degreeId;
    const gradYear = parseInt(edu.graduationYear, 10);
    const yearDiff = getCurrentYear() - gradYear;
    return (
      VALID_DEGREE_IDS.includes(degreeId) &&
      yearDiff >= 0 &&
      yearDiff <= 2 &&
      !!edu.docReferenceId &&
      !usedDocRefIds.has(edu.docReferenceId)
    );
  });

  const isStepValid = (step) => {
    if (step === 0)
      return !!selectedEducation && !!qualificationGrade && !!graduateDate;
    if (step === 1) return !!selectedCV;
    if (step === 2) return !!bank && !!accountNumber;
    return false;
  };

  const buildPreviewUrl = (docRefId) =>
    docRefId
      ? (process.env.REACT_APP_FILES_URL || "") + "/preview/" + docRefId
      : null;

  const handlePreviewDegree = () => {
    const url = buildPreviewUrl(selectedEducation?.docReferenceId);
    if (url) {
      setPreviewDocument({
        name: selectedEducation?.qualification?.name || "Degree document",
        url,
      });
      setShowDocument(true);
    }
  };

  const handlePreviewCV = () => {
    const url = buildPreviewUrl(
      typeof selectedCV === "string" ? selectedCV : null,
    );
    if (url) {
      setPreviewDocument({ name: "Curriculum Vitae", url });
      setShowDocument(true);
    }
  };

  const handleSubmit = () => {
    const gradYear = parseInt(selectedEducation?.graduationYear, 10);
    if (getCurrentYear() - gradYear > 2) {
      setFormError({
        msg: "Your graduation is more than 2 years ago. You are not eligible to apply.",
      });
      return;
    }
    setFormError(null);

    const formData = new FormData();
    formData.append(
      "schoolId",
      selectedEducation.school?.id || selectedEducation.schoolId || "",
    );
    formData.append(
      "degreeId",
      selectedEducation.qualification?.degree?.id || "",
    );
    // graduationYear stays as-is from the education record
    formData.append("graduationYear", selectedEducation.graduationYear);
    // graduateDate day/month chosen by user; year is from education record
    if (graduateDate) {
      formData.append(
        "graduationDate",
        new Date(graduateDate).toISOString().slice(0, 10),
      );
    }
    formData.append(
      "qualificationId",
      selectedEducation.qualification?.id ||
        selectedEducation.qualificationId ||
        "",
    );
    formData.append("qualificationGradeId", qualificationGrade.id);
    formData.append("bankId", bank?.id);
    formData.append("accountNumber", accountNumber);
    formData.append("useExistingDegree", "true");
    formData.append(
      "degreeDocReferenceId",
      String(selectedEducation.docReferenceId),
    );

    if (selectedCV) {
      formData.append("useExistingCV", "true");
      formData.append("cvDocReferenceId", String(selectedCV));
    }

    addInternshipApplication(formData, history);
  };

  // ── Step renderers ──────────────────────────────────────────────

  const renderStep1 = () => (
    <div>
      <Typography variant="subtitle1" className="mb-3 font-weight-bold">
        Choose an education background for this application
      </Typography>

      {loading && !userEducations?.length ? (
        <div className="text-center py-4">
          <CircularProgress size={32} />
        </div>
      ) : eligibleEducations.length === 0 ? (
        <div>
          <Alert severity="info" className="mb-3">
            No eligible education found in your profile. To qualify, your
            education must:
            <ul className="mt-1 mb-0">
              <li>Be of degree type: A1, A0, Master&apos;s, or PhD</li>
              <li>Have a graduation year within the last 2 years</li>
              <li>Have a degree document attached</li>
            </ul>
          </Alert>
          <Button
            variant="outlined"
            startIcon={<AddRoundedIcon />}
            onClick={() => setShowAddEducation(true)}
          >
            Add Education to Profile
          </Button>
        </div>
      ) : (
        <div>
          {eligibleEducations.map((edu) => {
            const degreeId = edu.qualification?.degree?.id ?? edu.degreeId;
            const isSelected = selectedEducation?.id === edu.id;
            return (
              <Card
                key={edu.id}
                variant="outlined"
                className="mb-2"
                style={{
                  borderColor: isSelected ? "#1976d2" : "#e0e0e0",
                  borderWidth: isSelected ? 2 : 1,
                  cursor: "pointer",
                  backgroundColor: isSelected ? "#f0f7ff" : "#fff",
                  transition: "all 0.15s",
                }}
                onClick={() => {
                  setSelectedEducation(edu);
                  setGraduateDate(
                    new Date(parseInt(edu.graduationYear, 10), 0, 1),
                  );
                  setFormError(null);
                }}
              >
                <CardHeader
                  avatar={
                    isSelected ? (
                      <CheckCircleIcon color="primary" />
                    ) : (
                      <RadioButtonUncheckedIcon
                        style={{ color: "#9e9e9e" }}
                      />
                    )
                  }
                  title={
                    <Typography variant="subtitle2" fontWeight={600}>
                      {edu.qualification?.name}
                    </Typography>
                  }
                  subheader={
                    <span>
                      {edu.school?.name} &bull;{" "}
                      {edu.school?.country?.name} &bull;{" "}
                      <strong>{edu.graduationYear}</strong>
                    </span>
                  }
                  action={
                    <Chip
                      size="small"
                      label={DEGREE_LABELS[degreeId] || ""}
                      color={isSelected ? "primary" : "default"}
                      className="mt-2 mr-1"
                    />
                  }
                />
              </Card>
            );
          })}

          <Button
            variant="text"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => setShowAddEducation(true)}
            className="mt-1"
          >
            Add new education
          </Button>

          <Alert severity="info" className="mt-3" sx={{ py: 0.5 }}>
            Need to update your education or graduation details?{" "}
            <a href="/applicant/profile">Go to your profile</a> to make
            changes, then return here.
          </Alert>
        </div>
      )}

      {selectedEducation && (
        <div className="mt-3 pt-3" style={{ borderTop: "1px solid #e0e0e0" }}>
          <div className="row">
            <div className="form-group col-12 col-md-6">
              <Typography variant="subtitle2" className="mb-2">
                Qualification Grade
              </Typography>
              <Autocomplete
                size="small"
                value={qualificationGrade}
                options={lookup.qualificationGrades || []}
                disabled={loading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, grade) => setQualificationGrade(grade || null)}
                getOptionLabel={(option) => option.name || ""}
                renderOption={(props, grade) => (
                  <Box component="li" {...props} key={grade.id}>
                    {grade.name}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Select qualification grade"
                  />
                )}
              />
            </div>

            <div className="form-group col-12 col-md-6">
              <Typography variant="subtitle2" className="mb-2">
                Graduation Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  label="Graduation date"
                  inputFormat="yyyy-MM-dd"
                  value={graduateDate}
                  minDate={
                    new Date(
                      parseInt(selectedEducation.graduationYear, 10),
                      0,
                      1,
                    )
                  }
                  maxDate={
                    new Date(
                      parseInt(selectedEducation.graduationYear, 10),
                      11,
                      31,
                    )
                  }
                  onChange={(newValue) => setGraduateDate(newValue || null)}
                  renderInput={(params) => (
                    <TextField
                      onKeyDown={(e) => e.preventDefault()}
                      {...params}
                      size="small"
                      className="w-100"
                      placeholder="Graduation date"
                    />
                  )}
                />
              </LocalizationProvider>
              <Typography variant="caption" color="text.secondary">
                Year is fixed to {selectedEducation.graduationYear} from your
                education record.
              </Typography>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div>
      <Typography variant="subtitle1" className="mb-3 font-weight-bold">
        Select your Curriculum Vitae
      </Typography>

      {userCV ? (
        <Card
          variant="outlined"
          className="mb-2"
          style={{
            borderColor: selectedCV ? "#1976d2" : "#e0e0e0",
            borderWidth: selectedCV ? 2 : 1,
            cursor: "pointer",
            backgroundColor: selectedCV ? "#f0f7ff" : "#fff",
            maxWidth: 480,
            transition: "all 0.15s",
          }}
          onClick={() => setSelectedCV(userCV)}
        >
          <CardHeader
            avatar={
              selectedCV ? (
                <CheckCircleIcon color="primary" />
              ) : (
                <RadioButtonUncheckedIcon style={{ color: "#9e9e9e" }} />
              )
            }
            title={
              <Typography variant="subtitle2" fontWeight={600}>
                Profile CV
              </Typography>
            }
            subheader="Your uploaded CV from profile"
            action={
              <Button
                size="small"
                variant="text"
                className="mt-1"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreviewCV();
                }}
              >
                Preview
              </Button>
            }
          />
        </Card>
      ) : (
        <Alert severity="warning">
          No CV found in your profile. Please{" "}
          <a href="/applicant/profile">go to your profile</a> and upload your
          CV first, then return here to continue.
        </Alert>
      )}

      <Alert severity="info" className="mt-3" sx={{ py: 0.5 }}>
        Need to update or replace your CV?{" "}
        <a href="/applicant/profile">Go to your profile</a> to upload a new
        one, then return here.
      </Alert>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <Typography variant="subtitle1" className="mb-3 font-weight-bold">
        Enter your bank details for internship payment
      </Typography>
      <div className="row">
        <div className="form-group col-12 col-md-6">
          <Autocomplete
            size="small"
            value={bank}
            options={lookup.banks || []}
            disabled={loading}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, b) => setBank(b || null)}
            getOptionLabel={(option) => option.name || ""}
            renderOption={(props, b) => (
              <Box component="li" {...props} key={b.id}>
                {b.name}
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Bank" />
            )}
          />
        </div>
        <div className="form-group col-12 col-md-6">
          <TextField
            size="small"
            className="w-100"
            value={accountNumber || ""}
            onChange={(e) => setAccountNumber(e.target.value)}
            label="Bank Account Number"
            placeholder="Enter your bank account number"
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div>
      <Typography variant="subtitle1" className="mb-3 font-weight-bold">
        Review your application details before submitting
      </Typography>

      <Alert severity="warning" className="mb-4">
        <strong>Warning:</strong> Please review all details carefully. Once
        submitted, you cannot edit this application unless it is sent back for
        correction.
      </Alert>

      <div className="row">
        <div className="col-12 col-md-6 mb-4">
          <Typography
            variant="overline"
            display="block"
            className="text-muted font-weight-bold"
          >
            Education Background
          </Typography>
          <Divider className="mb-2" />
          <Typography variant="body2">
            <strong>Institution:</strong>{" "}
            {selectedEducation?.school?.name || "-"}
          </Typography>
          <Typography variant="body2">
            <strong>Country:</strong>{" "}
            {selectedEducation?.school?.country?.name || "-"}
          </Typography>
          <Typography variant="body2">
            <strong>Degree:</strong>{" "}
            {selectedEducation?.qualification?.degree?.name || "-"}
          </Typography>
          <Typography variant="body2">
            <strong>Qualification:</strong>{" "}
            {selectedEducation?.qualification?.name || "-"}
          </Typography>
          <Typography variant="body2">
            <strong>Grade:</strong> {qualificationGrade?.name || "-"}
          </Typography>
          <Typography variant="body2">
            <strong>Graduation Year:</strong>{" "}
            {selectedEducation?.graduationYear || "-"}
          </Typography>
          <Typography variant="body2">
            <strong>Graduation Date:</strong>{" "}
            {graduateDate
              ? new Date(graduateDate).toISOString().slice(0, 10)
              : "-"}
          </Typography>
          <Button
            size="small"
            variant="text"
            className="mt-1 px-0"
            onClick={handlePreviewDegree}
          >
            Preview Degree Document
          </Button>
        </div>

        <div className="col-12 col-md-6 mb-4">
          <Typography
            variant="overline"
            display="block"
            className="text-muted font-weight-bold"
          >
            Curriculum Vitae
          </Typography>
          <Divider className="mb-2" />
          {selectedCV ? (
            <>
              <Typography variant="body2">Profile CV attached</Typography>
              <Button
                size="small"
                variant="text"
                className="mt-1 px-0"
                onClick={handlePreviewCV}
              >
                Preview CV
              </Button>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No CV attached
            </Typography>
          )}
        </div>

        <div className="col-12 col-md-6 mb-4">
          <Typography
            variant="overline"
            display="block"
            className="text-muted font-weight-bold"
          >
            Bank Details
          </Typography>
          <Divider className="mb-2" />
          <Typography variant="body2">
            <strong>Bank:</strong> {bank?.name || "-"}
          </Typography>
          <Typography variant="body2">
            <strong>Account Number:</strong> {accountNumber || "-"}
          </Typography>
        </div>
      </div>

      {formError && (
        <Alert severity="error" className="mb-3">
          {formError.msg}
        </Alert>
      )}

      <Button
        variant="contained"
        size="large"
        startIcon={<SendRoundedIcon />}
        onClick={handleSubmit}
        disabled={loading}
        className="px-5"
      >
        {loading ? "Submitting..." : "Submit Application"}
      </Button>
    </div>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      case 2:
        return renderStep3();
      case 3:
        return renderStep4();
      default:
        return null;
    }
  };

  return (
    <div className="row mx-0 mt-4">
      <div className="col-12 col-sm-8 col-md-9 order-last order-sm-first mt-sm-0 px-1 px-sm-3">
        <section className="card mb-3 elevated rounded border border-primary bg-light">
          <div className="card-header bg-light rounded-top font-weight-bold">
            <div className="my-1 d-flex flex-column flex-md-row justify-content-md-between align-items-center">
              <Typography
                variant="h6"
                component="h2"
                className="text-uppercase"
                aria-labelledby="new-internship-application"
              >
                <span className="mr-2">New Internship Application</span>
              </Typography>
              <Button
                variant="text"
                onClick={() =>
                  history.push("/applicant/internship-applications")
                }
                aria-label="Back to Applications"
              >
                Back to Applications
              </Button>
            </div>
          </div>

          <div className="card-body">
            <Stepper activeStep={activeStep} alternativeLabel className="mb-4">
              {STEPS.map((label, index) => (
                <Step
                  key={label}
                  completed={isStepValid(index) && index < activeStep}
                >
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <div className="p-2 p-md-3">{renderStepContent()}</div>

            <div className="d-flex justify-content-between align-items-center mt-4 px-2 px-md-3 pb-2">
              <Button
                variant="outlined"
                onClick={() => setActiveStep((s) => s - 1)}
                disabled={activeStep === 0}
              >
                Back
              </Button>
              {activeStep < STEPS.length - 1 && (
                <Button
                  variant="contained"
                  onClick={() => setActiveStep((s) => s + 1)}
                  disabled={!isStepValid(activeStep) || loading}
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </section>
      </div>

      <aside className="col-12 mb-3 col-sm-4 col-md-3 order-first order-sm-last px-1 px-sm-3">
        <ProfileSummary />
      </aside>

      {showAddEducation && (
        <EducationFormDialog
          onShow={showAddEducation}
          selectedEducation={null}
          onClose={() => setShowAddEducation(false)}
        />
      )}

      {showDocument && previewDocument && (
        <PreviewPdfUrl
          showDocument={showDocument}
          setShowDocument={() => setShowDocument(false)}
          document={previewDocument}
          isBase64={false}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({
  user,
  loading,
  onWait,
  lookup,
  userEducations,
  userCV,
  internshipApplications,
}) => ({
  user,
  loading,
  onWait,
  lookup,
  userEducations,
  userCV,
  internshipApplications,
});

export default connect(mapStateToProps, {
  getQualificationGrades,
  getUserEducations,
  getUserCV,
  addInternshipApplication,
  getInternshipApplications,
  getTime,
  getBanks,
})(NewInternshipApplication);
